import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import classes from "./PartnersSectionNew.module.scss";
import cs from "classnames";
import { StoreContext } from "../../../contexts";
import {
    APPLICATION_ROUTES,
    PARTNERSHIP_STATUS,
    PROJECTS,
} from "../../../constants";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import partnerIcon from "../../../assets/images/partner-icon.png";
import { ACTIONS } from "../../../store";
import Modal from "../../organisms/Modal";
import BusinessSection from "../BusinessSection/BusinessSection";
import Icon, { NAMES, SIZES } from "../../atoms/Icon";
import Tooltip from "../../molecules/Tooltip";

const hasActivePlan = (details) =>
    !!details &&
    (details["status"] == PARTNERSHIP_STATUS.ACTIVE ||
        details["status"] == PARTNERSHIP_STATUS.PENDING ||
        Date.now() < new Date(details["expiration_date"]));

const PartnersSectionNew = observer(({ discount = false, noWrapper = false }) => {
    const { partnership, authorization, common } = useContext(StoreContext);
    const history = useHistory();

    const [monthlyPlan, setMonthlyPlan] = useState(false);
    const [addCardBasic, setAddCardBasic] = useState(false);
    const [addCardBirthday, setAddCardBirthday] = useState(false);
    const [activeCard, setActiveCard] = useState(false);

    const [currentPlan, setCurrentPlan] = useState("");
    const [currentPlanBillingInterval, setCurrentPlanBillingInterval] = useState("");
    const [showBusinessModal, setShowBusinessModal] = useState(false);

    // Refetch on sign-in too, so the auto-join below sees the member's plan
    useEffect(() => {
        partnership[`${ACTIONS.PARTNERSHIP.GET_PARTNERSHIP}`]();
    }, [authorization.isAuthenticated]);

    useEffect(() => {
        partnership[`${ACTIONS.PARTNERSHIP.GET_ALL_BUSINESS_PARTNERS}`]();
    }, []);

    useEffect(() => {
        if (hasActivePlan(partnership.details)) {
            const planId = partnership.details["plan"].toLowerCase().replace("-", "_");
            setCurrentPlan(planId);
            setCurrentPlanBillingInterval(partnership.details["interval"]);
            if (partnership.details["interval"] == 1) {
                setMonthlyPlan(true);
            }
            if (planId == PROJECTS.PARTNERS.PACKAGES.PARTNER_TIER.ID) {
                setAddCardBasic(true);
                setActiveCard(true);
            }
            if (planId == PROJECTS.PARTNERS.PACKAGES.SILVER_CARD_TIER.ID) {
                setAddCardBirthday(true);
                setActiveCard(true);
            }
        }
    }, [partnership.details]);

    // Auto-join after sign-in if plan was selected but user was not logged in
    const isLoadingPartnership = partnership[`isLoading${ACTIONS.PARTNERSHIP.GET_PARTNERSHIP}`];
    useEffect(() => {
        const storedPlan = sessionStorage.getItem('selectedPlan');
        const storedCycle = sessionStorage.getItem('selectedBillingCycle');

        // Wait for the member's partnership to load before deciding. Read the store directly:
        // the fetch effect above starts it after this render's values were taken, and
        // currentPlan state only catches up a render after the details arrive.
        if (partnership[`isLoading${ACTIONS.PARTNERSHIP.GET_PARTNERSHIP}`]) return;

        if (authorization.isAuthenticated && !authorization.isLoadingSignIn && (common.selectedPlan || storedPlan) && (common.selectedBillingCycle || storedCycle)) {
            const planToJoin = common.selectedPlan || storedPlan;
            const cycleToJoin = common.selectedBillingCycle || storedCycle;

            // Only auto-join if they don't have a plan yet
            if (!hasActivePlan(partnership.details)) {
                onSubmitAndNavigateToPayment({
                    projectType: planToJoin,
                    billingCycle: parseInt(cycleToJoin)
                });
            }
            // Clear the stored plan/cycle to avoid re-triggering
            common.set('selectedPlan', null);
            common.set('selectedBillingCycle', null);
            sessionStorage.removeItem('selectedPlan');
            sessionStorage.removeItem('selectedBillingCycle');
        }
    }, [authorization.isAuthenticated, isLoadingPartnership, partnership.details]);

    // If monthly is selected on basic, force card off
    useEffect(() => {
        if (monthlyPlan) {
            setAddCardBasic(false);
        }
    }, [monthlyPlan]);

    const onSubmitAndNavigateToPayment = ({ projectType, billingCycle }) => {
        const pageParam = discount && projectType === PROJECTS.PARTNERS.PACKAGES.BASIC_TIER.ID && billingCycle === 12
            ? ''
            : '';

        common.set('selectedPlan', projectType);
        common.set('selectedBillingCycle', billingCycle);

        if (authorization.user === null) {
            sessionStorage.setItem('selectedPlan', projectType);
            sessionStorage.setItem('selectedBillingCycle', billingCycle);
            if (location.href.includes(APPLICATION_ROUTES.NINETEEN_KISLEV)) {
                history.push(
                    `${APPLICATION_ROUTES.SIGN_IN}?redirect=${APPLICATION_ROUTES.NINETEEN_KISLEV}`
                );
            } else {
                history.push(
                    `${APPLICATION_ROUTES.SIGN_IN}?redirect=${APPLICATION_ROUTES.PARTNERS_PACKAGES}`
                );
            }
        } else {
            if (
                (currentPlan == PROJECTS.PARTNERS.PACKAGES.PARTNER_TIER.ID ||
                    currentPlan == PROJECTS.PARTNERS.PACKAGES.SILVER_CARD_TIER.ID) &&
                (projectType == PROJECTS.PARTNERS.PACKAGES.SILVER_CARD_TIER.ID ||
                    projectType == PROJECTS.PARTNERS.PACKAGES.GOLD_TIER.ID)
            ) {
                history.push(
                    `${APPLICATION_ROUTES.CHECKOUT}/${projectType}/${billingCycle}/partnership?delivery=false${pageParam}`
                );
            } else {
                if (
                    projectType == PROJECTS.PARTNERS.PACKAGES.BASIC_TIER.ID ||
                    projectType == PROJECTS.PARTNERS.PACKAGES.SILVER_TIER.ID
                ) {
                    history.push(
                        `${APPLICATION_ROUTES.CHECKOUT}/${projectType}/${billingCycle}/partnership?${pageParam.replace('&', '')}`
                    );
                } else {
                    history.push(
                        `${APPLICATION_ROUTES.PARTNERS_ORDER}/${projectType}/${billingCycle}`
                    );
                }
            };
            sessionStorage.removeItem('selectedPlan');
            sessionStorage.removeItem('selectedBillingCycle');
            common.set('selectedPlan', null);
            common.set('selectedBillingCycle', null);
        }
    };

    const isCurrentPlan = (planId, interval) => {
        return currentPlan === planId && currentPlanBillingInterval == interval;
    };

    const renderJoinButton = (planId, interval, buttonClass) => {
        const isCurrent = isCurrentPlan(planId, interval);
        const hasAnyPlan = currentPlan !== "";

        // Hide if not current plan and user has a plan
        if (hasAnyPlan && !isCurrent) {
            return (
                <button
                    className={cs(
                        classes.joinBtn,
                        buttonClass,
                        classes.btnDisabled
                    )}
                    disabled={true}
                >
                    JOIN NOW
                </button>
            )
        }

        if (isCurrent) {
            return (
                <button
                    className={cs(
                        classes.joinBtn,
                        buttonClass,
                        classes.btnCurrent
                    )}
                // disabled={true}
                >
                    YOUR CURRENT PLAN
                </button>
            )
        }

        return (
            <button
                className={cs(
                    classes.joinBtn,
                    buttonClass)}
                onClick={() => {
                    onSubmitAndNavigateToPayment({
                        projectType: planId,
                        billingCycle: interval,
                    });
                }}
            >
                JOIN NOW
            </button>
        );
    };

    return (
        <div className={cs(classes.partnersSection, !noWrapper && "wrapper-xl", noWrapper && classes.noWrapper)}>

            {/* 1. MYMAOR BASIC */}
            <div className={classes.planCard}>
                <div className={cs(classes.planTitle, classes.titleYellow)}>
                    MYMAOR BASIC
                </div>

                <div className={classes.featuresList}>
                    <div className={classes.featureItem}>Weekly Video</div>
                    <div className={classes.featureItem}>Full Library access</div>
                    <div className={classes.featureItem}>Coloring Pages</div>
                </div>

                {/* Hide add card box if user has ANY active plan EXCEPT if they own this specific card tier */}
                {/* {(currentPlan === "" || isCurrentPlan(PROJECTS.PARTNERS.PACKAGES.PARTNER_TIER.ID, 12)) && ( */}
                <div
                    className={cs(
                        classes.addOnBox,
                        addCardBasic && classes.active,
                        activeCard && classes.activeCard,
                        monthlyPlan && classes.disabled
                    )}
                >
                    <div className={classes.addOnInfo}>
                        <img src={partnerIcon} alt="Info" className={classes.infoIcon} onClick={() => setShowBusinessModal(true)} />
                        <div className={classes.addOnDetails}>
                            <span className={classes.addOnText}>Maor Partner Card</span>
                            <span className={classes.addOnPrice}>+ $144/Year</span>
                        </div>
                    </div>
                    <button
                        className={classes.addBtn}
                        onClick={() => {
                            if (!monthlyPlan) setAddCardBasic(!addCardBasic);
                        }}
                        disabled={monthlyPlan}
                    >
                        {addCardBasic ? "REMOVE" : "ADD"}
                    </button>
                </div>
                {/* )} */}

                <div className={classes.divider} />

                <div className={cs(classes.billingToggle, currentPlan !== "" && classes.disabled)}>
                    <div
                        className={cs(classes.toggleOption, !monthlyPlan && classes.active)}
                        onClick={() => {
                            if (currentPlan === "") setMonthlyPlan(false);
                        }}
                    >
                        Yearly
                    </div>
                    <div
                        className={cs(classes.toggleOption, monthlyPlan && classes.active)}
                        onClick={() => {
                            if (currentPlan === "") setMonthlyPlan(true);
                        }}
                    >
                        Monthly
                    </div>
                </div>

                <div className={classes.priceContainer}>
                    <div className={classes.priceText}>
                        {monthlyPlan
                            ? `$${PROJECTS.PARTNERS.PACKAGES.BASIC_TIER.COST_MONTH}/Month`
                            : `$${addCardBasic ? PROJECTS.PARTNERS.PACKAGES.PARTNER_TIER.COST_YEAR : PROJECTS.PARTNERS.PACKAGES.BASIC_TIER.COST_YEAR}/Year`
                        }
                    </div>
                </div>

                {monthlyPlan ? (
                    renderJoinButton(PROJECTS.PARTNERS.PACKAGES.BASIC_TIER.ID, 1, classes.btnYellow)
                ) : (
                    renderJoinButton(
                        addCardBasic ? PROJECTS.PARTNERS.PACKAGES.PARTNER_TIER.ID : PROJECTS.PARTNERS.PACKAGES.BASIC_TIER.ID,
                        12,
                        classes.btnYellow
                    )
                )}
            </div>

            {/* 2. MYMAOR BIRTHDAY (HIGHLIGHT) */}
            <div className={cs(classes.planCard)}>
                <div className={classes.bestValueBadge}>BEST VALUE!</div>

                <div className={cs(classes.planTitle, classes.titlePink)}>
                    MYMAOR BIRTHDAY
                </div>

                <div className={classes.featuresList}>
                    <div className={classes.featureItem}>Weekly Video</div>
                    <div className={classes.featureItem}>Full Library access</div>
                    <div className={classes.featureItem}>Coloring Pages</div>
                    <div className={classes.featureItem}>
                        4 Birthdays
                        <Tooltip message="$216 worth of credit">
                            <span className={classes.tooltipIcon}>?</span>
                        </Tooltip>
                    </div>
                </div>

                {/* Hide add card box if user has ANY active plan EXCEPT if they own this specific card tier */}
                {/* {(currentPlan === "" || isCurrentPlan(PROJECTS.PARTNERS.PACKAGES.SILVER_CARD_TIER.ID, 12)) && ( */}
                <div className={cs(classes.addOnBox, addCardBirthday && classes.active, classes.addOnPink, activeCard && classes.activeCard)}>
                    <div className={classes.addOnInfo}>
                        <img src={partnerIcon} alt="Info" className={classes.infoIcon} onClick={() => setShowBusinessModal(true)} />
                        <div className={classes.addOnDetails}>
                            <span className={classes.addOnText}>Maor Partner Card</span>
                            <span className={classes.addOnPrice}>+ $105/Year</span>
                        </div>
                    </div>
                    {/* {currentPlan === "" && ( */}
                    <button
                        className={cs(classes.addBtn, classes.btnPink)}
                        onClick={() => setAddCardBirthday(!addCardBirthday)}
                    >
                        {addCardBirthday ? "REMOVE" : "ADD"}
                    </button>
                    {/* )} */}
                </div>
                {/* )} */}

                <div className={classes.divider} />

                <div className={classes.priceContainer}>
                    <div className={classes.priceText}>
                        ${addCardBirthday ? PROJECTS.PARTNERS.PACKAGES.SILVER_CARD_TIER.COST_YEAR : PROJECTS.PARTNERS.PACKAGES.SILVER_TIER.COST_YEAR}/Year
                    </div>
                </div>

                {renderJoinButton(
                    addCardBirthday ? PROJECTS.PARTNERS.PACKAGES.SILVER_CARD_TIER.ID : PROJECTS.PARTNERS.PACKAGES.SILVER_TIER.ID,
                    12,
                    classes.btnPink
                )}
            </div>

            {/* 3. MYMAOR PREMIUM */}
            <div className={classes.planCard}>
                <div className={cs(classes.planTitle, classes.titleOrange)}>
                    MYMAOR PREMIUM
                </div>

                <div className={classes.featuresList}>
                    <div className={classes.featureItem}>Weekly Video</div>
                    <div className={classes.featureItem}>Full Library access</div>
                    <div className={classes.featureItem}>Coloring Pages</div>
                    <div className={classes.featureItem}>
                        All Family Birthdays
                        <Tooltip message="$576 worth of credit">
                            <span className={classes.tooltipIcon}>?</span>
                        </Tooltip>
                    </div>
                    <div className={classes.featureItem}>Maor Partner Card</div>
                    <div className={classes.featureItem}>All New MyMaor Books</div>
                </div>

                <div className={classes.divider} style={{ marginTop: "auto" }} />

                <div className={classes.priceContainer}>
                    <div className={classes.priceText}>
                        ${PROJECTS.PARTNERS.PACKAGES.GOLD_TIER.COST_YEAR}/Year
                    </div>
                </div>

                {renderJoinButton(PROJECTS.PARTNERS.PACKAGES.GOLD_TIER.ID, 12, classes.btnOrange)}
            </div>

            <Modal opened={showBusinessModal} onClose={() => setShowBusinessModal(false)}>
                <div className={classes.businessModal}>
                    <div
                        onClick={() => setShowBusinessModal(false)}
                        className={classes.closeModal}
                    >
                        <Icon name={NAMES.Cross} size={SIZES.S} />
                    </div>
                    <BusinessSection hideBottom={true} />
                </div>
            </Modal>
        </div>
    );
});

export default PartnersSectionNew;
